import { useRef } from "react";

import { Slide } from "react-awesome-reveal";
import { useHistoryToShow } from "@doitintl/ava-components/src/Messenger/hooks";
import { Stack } from "@mui/material";

import { useAvaConversation } from "../Ava/useAvaConversation";
import { MessageAvaComponent } from "./MessageAvaComponent";
import { MessageUserComponent } from "./MessageUserComponent";
import { PresetFooter } from "./PresetFooter";
import { useScroll } from "./scrollHooks";
import { type MessageAva, type MessageUser } from "./types";

type Props = {
  conversationId?: string;
  handleAskQuestion: (question: string) => Promise<void>;
  messageUserInProgress: MessageUser;
  processingQuestion: boolean;
  messageAvaInProgress: MessageAva;
  error?: string;
  isGeneratingReport: boolean;
  newQuestionAsked: boolean;
  avaChatLoading?: string;
};

export const MessageList = ({
  conversationId,
  handleAskQuestion,
  messageUserInProgress,
  processingQuestion,
  messageAvaInProgress,
  error,
  isGeneratingReport,
  newQuestionAsked,
  avaChatLoading,
}: Props) => {
  const stackRef = useRef<HTMLDivElement>(null);

  const { handleScroll } = useScroll(stackRef, messageAvaInProgress);

  const [conversationHistory, loadingHistory] = useAvaConversation(conversationId);

  const historyToShow = useHistoryToShow(
    messageUserInProgress.content,
    processingQuestion,
    conversationHistory,
    messageAvaInProgress.answerId,
    newQuestionAsked
  );

  const handleRetryClicked = async () => {
    const question = messageUserInProgress.content;
    if (question.length > 0) {
      await handleAskQuestion(question);
    }
  };

  if (loadingHistory && !processingQuestion) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
        <img src="/doit-loading.gif" alt="Loading" style={{ height: 170 }} />
      </Stack>
    );
  }

  if (!conversationId && !processingQuestion) {
    return <PresetFooter onAskQuestion={handleAskQuestion} />;
  }

  return (
    <Stack
      onScroll={handleScroll}
      ref={stackRef}
      sx={{ overflow: "hidden auto", scrollbarWidth: "none", px: 0, py: 2 }}
    >
      {historyToShow.map((message) => {
        if (message.role === "user") {
          return <MessageUserComponent key={message.createdAt.getTime()} message={message} disableAnimation={true} />;
        }
        if (message.role === "ava") {
          return (
            <MessageAvaComponent
              key={message.createdAt.getTime()}
              message={message}
              disableAnimation={true}
              handleAskQuestion={handleAskQuestion}
              avaChatLoading={avaChatLoading}
            />
          );
        }
      })}
      {newQuestionAsked && (
        <Slide direction="up" duration={300} triggerOnce>
          <MessageUserComponent message={messageUserInProgress} disableAnimation={false} />
          <MessageAvaComponent
            message={messageAvaInProgress}
            disableAnimation={false}
            handleAskQuestion={handleAskQuestion}
            onRetryClicked={handleRetryClicked}
            showError={error}
            isGeneratingReport={isGeneratingReport}
            avaChatLoading={avaChatLoading}
          />
        </Slide>
      )}
    </Stack>
  );
};
